import { useEffect, useRef, useState } from "react";
import "./captcha.css";

interface CaptchaProps {
  config: {
    type: number;
    length: number;
    font: {
      size?: string;
      family?: string;
      color: string;
    };
    back: {
      solid: string;
      stroke?: string;
    };
    buttonColor?: string;
  };
  onCaptchaStatus: (status: boolean) => void;
}

const AppCaptcha = ({ config, onCaptchaStatus }: CaptchaProps) => {
  const [isSpinning, setIsSpinning] = useState(false);
  const [captchaInput, setCaptchaInput] = useState("");
  const [code, setCode] = useState("");
  const [resultCode, setResultCode] = useState("");
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (config) createCaptcha();
  }, [config]);

  const createCaptcha = () => {
    setIsSpinning(true);
    let generatedCode = "";
    let generatedResult = "";

    if (config.type === 1) {
      // Alphanumeric captcha
      generatedCode = (
        Math.random().toString(24).substring(2, config.length) +
        Math.random().toString(24).substring(2, 4)
      ).toUpperCase();
      generatedResult = generatedCode;
    } else if (config.type === 2) {
      // Math captcha
      const num1 = Math.floor(Math.random() * 99);
      const num2 = Math.floor(Math.random() * 9);
      const operators = ["+", "-"];
      const operator = operators[Math.floor(Math.random() * operators.length)];
      generatedCode = `${num1} ${operator} ${num2} = ?`;
      generatedResult = (
        operator === "+" ? num1 + num2 : num1 - num2
      ).toString();
    }

    setCode(generatedCode);
    setResultCode(generatedResult);
    setCaptchaInput(""); // Reset input when creating new captcha

    setTimeout(() => {
      setIsSpinning(false);
      drawCaptcha(generatedCode);
    }, 100);
  };

  const drawCaptcha = (text: string) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.fillStyle = config.back.solid;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.beginPath();
    canvas.style.letterSpacing = "15px";
    ctx.font = `${config.font.size} ${config.font.family}`;
    ctx.fillStyle = config.font.color;
    ctx.textBaseline = "middle";
    ctx.fillText(text, 40, 50);

    if (config.back.stroke) {
      ctx.strokeStyle = config.back.stroke;
      for (let i = 0; i < 150; i++) {
        ctx.moveTo(Math.random() * 300, Math.random() * 300);
        ctx.lineTo(Math.random() * 300, Math.random() * 300);
      }
      ctx.stroke();
    }
  };

  const playCaptcha = () => {
    const msg = new SpeechSynthesisUtterance(code.split("").join(" "));
    msg.pitch = 0.1;
    window.speechSynthesis.speak(msg);
  };

  const checkCaptcha = () => {
    if (captchaInput !== resultCode.toString()) {
      onCaptchaStatus(false);
      createCaptcha();
    } else {
      onCaptchaStatus(true);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      checkCaptcha();
    }
  };

  return (
    <div className="captcha-container">
      <canvas
        ref={canvasRef}
        width="300"
        height="100"
        onClick={playCaptcha}
        title="Click to hear the captcha"
      />
      <div className="captcha-actions">
        <input
          type="text"
          value={captchaInput}
          onChange={(e) => setCaptchaInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Enter captcha"
          aria-label="Enter captcha"
        />
        <input
          className="btn btn-primary"
          type="button"
          value="Check"
          onClick={checkCaptcha}
        />
        <a
          href="#"
          className={`reload ${isSpinning ? "spin-once" : ""}`}
          onClick={(e) => {
            e.preventDefault();
            createCaptcha();
          }}
          title="Refresh captcha"
        >
          🔄
        </a>
      </div>
    </div>
  );
};

export default AppCaptcha;
